import React, { useMemo } from "react";
import { Paper, Typography, Chip, Stack, Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Cell,
} from "recharts";
import { formatearImporte } from "./pygAnaliticoBuild";
import { buildParetoSeries } from "./paretoMarcaUtils";
import { useTranslation } from "react-i18next";

const fmtPct = (v) =>
  typeof v === "number"
    ? `${v.toLocaleString("es-ES", { maximumFractionDigits: 1 })} %`
    : "—";

const fmtAxis = (v) =>
  typeof v === "number"
    ? v >= 1000
      ? `${Math.round(v / 1000)}k`
      : String(Math.round(v))
    : v;

const ParetoTooltip = ({ active, payload, valorLabel, metric }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <Paper sx={{ p: 1.25, maxWidth: 280 }} elevation={3}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, fontSize: "0.8rem" }}
      >
        #{d.rank} {d.marca}
      </Typography>
      <Typography variant="caption" display="block">
        {valorLabel}: {formatearImporte(d.valor)}
      </Typography>
      {metric === "venta" && (
        <Typography variant="caption" display="block" color="text.secondary">
          {t("pygAnalitico.marcasCharts.paretoBooked", {
            venta: formatearImporte(d.ventaContable),
            proy: formatearImporte(d.proyeccionVentas),
            ped: formatearImporte(d.pedidosVenta),
          })}
        </Typography>
      )}
      <Typography variant="caption" display="block">
        {t("pygAnalitico.marcasCharts.paretoShare", {
          pct: fmtPct(d.pctParte),
          acc: fmtPct(d.pctAcumulado),
        })}
      </Typography>
      {d.dentro80 && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ fontWeight: 600 }}
        >
          {t("pygAnalitico.marcasCharts.paretoInside80")}
        </Typography>
      )}
    </Paper>
  );
};

const ParetoMarcaChart = ({
  rows,
  metric,
  title,
  subtitle,
  valorLabel,
  barColor,
  onSelectMarca,
  maxChartPoints,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const pareto = useMemo(
    () => buildParetoSeries(rows, metric, maxChartPoints),
    [rows, metric, maxChartPoints],
  );

  const { chartPoints, marcasHasta80, pctCatalogo80, totalMarcas, truncated } =
    pareto;

  const yAxisMarcaWidth = useMemo(() => {
    const maxLen = chartPoints.reduce(
      (m, r) => Math.max(m, (r.marca || "").length),
      0,
    );
    return Math.min(140, Math.max(72, maxLen * 4.8));
  }, [chartPoints]);

  if (!chartPoints.length) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {t("pygAnalitico.marcasCharts.noPositive", { value: valorLabel })}
        </Typography>
      </Paper>
    );
  }

  const barHeight = 18;
  const chartHeight = Math.max(280, chartPoints.length * barHeight);
  const chartViewportMax = 420;
  const chartId = `pareto-${metric}`;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.75 }}
      >
        {subtitle}
        {truncated
          ? ` · ${t("pygAnalitico.marcasCharts.paretoTruncated", {
              shown: chartPoints.length,
              total: totalMarcas,
            })}`
          : null}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 1 }}
      >
        <Chip
          size="small"
          label={t("pygAnalitico.marcasCharts.paretoChip80", {
            count: marcasHasta80,
            value: valorLabel,
          })}
          sx={{
            fontWeight: 700,
            height: 22,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={t("pygAnalitico.marcasCharts.paretoCatalog", {
            pct: fmtPct(pctCatalogo80),
            count: totalMarcas,
          })}
          sx={{ height: 22 }}
        />
      </Stack>
      <Box
        sx={{
          maxHeight: chartViewportMax,
          overflowY: chartHeight > chartViewportMax ? "auto" : "visible",
          overflowX: "hidden",
          pr: 0.5,
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            id={chartId}
            layout="vertical"
            data={chartPoints}
            margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
            onClick={(state) => {
              const marca = state?.activePayload?.[0]?.payload?.marca;
              if (marca && onSelectMarca) onSelectMarca(marca);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              tickFormatter={fmtAxis}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              type="category"
              dataKey="marca"
              width={yAxisMarcaWidth}
              interval={0}
              tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
            />
            <RechartsTooltip
              content={
                <ParetoTooltip valorLabel={valorLabel} metric={metric} />
              }
            />
            <ReferenceLine x={0} stroke={theme.palette.divider} />
            <Bar
              dataKey="valor"
              name={valorLabel}
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              barSize={12}
              isAnimationActive={false}
            >
              {chartPoints.map((entry) => (
                <Cell
                  key={`${chartId}-${entry.marca}`}
                  fill={
                    entry.dentro80
                      ? barColor || theme.palette.primary.main
                      : alpha(theme.palette.grey[500], 0.45)
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, display: "block" }}
      >
        {t("pygAnalitico.marcasCharts.paretoFooter", {
          count: totalMarcas,
          value: valorLabel,
        })}
        {chartHeight > chartViewportMax
          ? ` · ${t("pygAnalitico.marcasCharts.paretoScroll")}`
          : ""}
      </Typography>
    </Paper>
  );
};

export default ParetoMarcaChart;
